/**
 * Reaction Bar
 *
 * A target-agnostic emoji reaction control for messages, comments, and
 * other interactive surfaces.
 *
 * Primary responsibilities:
 * - Render aggregate emoji reaction badges with active-user state
 * - Provide a compact fixed-palette emoji picker
 *
 * Dependencies:
 * - Existing UI primitives for buttons, popovers, and tooltips
 * - Framer Motion for add/remove badge animation
 */

'use client'

import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { SmilePlus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

export const REACTION_EMOJI_PALETTE = ['👍', '❤️', '😂', '🎉', '👀', '✅'] as const

export type ReactionEmoji = (typeof REACTION_EMOJI_PALETTE)[number] | (string & {})

export type ReactionSummary = {
  emoji: ReactionEmoji
  count: number
  reactedByCurrentUser?: boolean
}

export type ReactionBarProps = {
  reactions: readonly ReactionSummary[]
  onToggleReaction: (emoji: ReactionEmoji) => void | Promise<void>
  className?: string
  disabled?: boolean
  palette?: readonly ReactionEmoji[]
  showPicker?: boolean
  onOpenPicker?: () => void
}

function formatReactionCount(count: number) {
  if (count < 1000) return String(count)
  if (count < 1000000) return `${Math.floor(count / 100) / 10}k`
  return `${Math.floor(count / 100000) / 10}m`
}

function getReactionLabel(reaction: ReactionSummary) {
  const countLabel = reaction.count === 1 ? '1 reaction' : `${reaction.count} reactions`
  const stateLabel = reaction.reactedByCurrentUser ? ', selected' : ''

  return `${reaction.emoji} ${countLabel}${stateLabel}`
}

export function ReactionBar({
  reactions,
  onToggleReaction,
  className,
  disabled = false,
  palette = REACTION_EMOJI_PALETTE,
  showPicker = true,
  onOpenPicker
}: ReactionBarProps) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const visibleReactions = useMemo(
    () => reactions.filter((reaction) => reaction.count > 0),
    [reactions]
  )

  const handleOpenChange = (open: boolean) => {
    setPickerOpen(open)

    if (open) {
      onOpenPicker?.()
    }
  }

  const togglePickerReaction = (emoji: ReactionEmoji) => {
    void onToggleReaction(emoji)
    setPickerOpen(false)
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn('group flex max-w-full items-center gap-1', className)}
        data-picker-open={pickerOpen}
      >
        <div className="flex min-w-0 flex-wrap items-center -space-x-1.5">
          <AnimatePresence initial={false}>
            {visibleReactions.map((reaction) => (
              <motion.div
                key={reaction.emoji}
                layout
                initial={{ opacity: 0, scale: 0.82, y: 4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.82, y: 4 }}
                transition={{ duration: 0.16, ease: 'easeOut' }}
                className="relative inline-flex"
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      disabled={disabled}
                      aria-label={getReactionLabel(reaction)}
                      aria-pressed={reaction.reactedByCurrentUser}
                      onClick={() => void onToggleReaction(reaction.emoji)}
                      className={cn(
                        'bg-muted/45 hover:bg-muted/70 relative inline-flex h-7 min-w-10',
                        'items-center justify-center gap-1 rounded-full border px-2',
                        'text-muted-foreground text-xs shadow-sm transition-[background-color,border-color,box-shadow,transform]',
                        'focus-visible:ring-primary/40 focus-visible:z-10',
                        'focus-visible:outline-none focus-visible:ring-2',
                        'disabled:pointer-events-none disabled:opacity-50',
                        reaction.reactedByCurrentUser
                          ? 'border-primary/45 bg-primary/10 text-primary shadow-primary/10'
                          : 'border-border/70'
                      )}
                    >
                      <span className="text-[15px] leading-none" aria-hidden="true">
                        {reaction.emoji}
                      </span>
                      <motion.span
                        key={reaction.count}
                        initial={{ opacity: 0, y: -2 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="tabular-nums leading-none"
                      >
                        {formatReactionCount(reaction.count)}
                      </motion.span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{getReactionLabel(reaction)}</TooltipContent>
                </Tooltip>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {showPicker ? (
          <Popover open={pickerOpen} onOpenChange={handleOpenChange}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={disabled}
                    aria-label="Add reaction"
                    className={cn(
                      'bg-muted/40 text-muted-foreground hover:bg-muted/70',
                      'hover:text-foreground border-border/60 size-7 shrink-0',
                      'pointer-events-none rounded-full border opacity-0',
                      'transition-[opacity,background-color,color,border-color]',
                      'focus-visible:ring-primary/40 focus-visible:pointer-events-auto',
                      'focus-visible:opacity-100 focus-visible:ring-2',
                      'group-focus-within:pointer-events-auto group-focus-within:opacity-100',
                      'group-hover:pointer-events-auto group-hover:opacity-100',
                      'data-[open=true]:pointer-events-auto data-[open=true]:opacity-100'
                    )}
                    data-open={pickerOpen}
                  >
                    <SmilePlus className="size-4" />
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent>Add reaction</TooltipContent>
            </Tooltip>
            <PopoverContent
              align="start"
              side="top"
              className="w-auto rounded-lg p-1.5"
            >
              <div className="grid grid-cols-6 gap-1">
                {palette.map((emoji) => (
                  <Button
                    key={emoji}
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`React with ${emoji}`}
                    className="size-9 rounded-md text-lg"
                    onClick={() => togglePickerReaction(emoji)}
                  >
                    <span aria-hidden="true">{emoji}</span>
                  </Button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        ) : null}
      </div>
    </TooltipProvider>
  )
}
